import type {
  AdvancedSearchRequest,
  SearchFieldDto,
  SearchFieldType,
  SearchFilterGroup,
  SearchFilterRule,
  SearchOperator,
  SearchSort,
} from '@/shared/api';
import { SEARCH_SCHEMA_VERSION } from '@/shared/api';

export const OPERATOR_LABELS: Record<SearchOperator, string> = {
  Equals: 'ist',
  NotEquals: 'ist nicht',
  Contains: 'enthält',
  StartsWith: 'beginnt mit',
  In: 'ist eines von',
  LessThan: 'kleiner als',
  LessThanOrEqual: 'kleiner/gleich',
  GreaterThan: 'größer als',
  GreaterThanOrEqual: 'größer/gleich',
  Between: 'zwischen',
  IsEmpty: 'ist leer',
  IsNotEmpty: 'ist nicht leer',
};

/** Value-losе Operatoren (IsEmpty/IsNotEmpty) brauchen kein Eingabefeld. */
export function operatorNeedsValue(op: SearchOperator): boolean {
  return op !== 'IsEmpty' && op !== 'IsNotEmpty';
}

/** Between erwartet genau zwei Werte, In eine Liste. */
export function operatorIsRange(op: SearchOperator): boolean {
  return op === 'Between';
}

export function operatorIsList(op: SearchOperator): boolean {
  return op === 'In';
}

export type ValueInputKind = 'text' | 'number' | 'date' | 'datetime' | 'boolean' | 'enum';

export function valueInputKind(type: SearchFieldType): ValueInputKind {
  switch (type) {
    case 'Number':
    case 'Money':
    case 'Id':
      return type === 'Id' ? 'text' : 'number';
    case 'Date':
      return 'date';
    case 'DateTime':
      return 'datetime';
    case 'Boolean':
      return 'boolean';
    case 'Enum':
      return 'enum';
    default:
      return 'text';
  }
}

/** Leerer Startwert passend zum Operator (Range → 2er-Array, List → 1er-Array, sonst leer). */
export function defaultValueFor(op: SearchOperator): unknown {
  if (!operatorNeedsValue(op)) return undefined;
  if (operatorIsRange(op)) return ['', ''];
  if (operatorIsList(op)) return [''];
  return '';
}

export function defaultRuleFor(field: SearchFieldDto): SearchFilterRule {
  const operator = field.operators[0] ?? 'Equals';
  return { field: field.key, operator, value: defaultValueFor(operator) };
}

export function emptyGroup(): SearchFilterGroup {
  return { logic: 'And', rules: [], groups: [] };
}

/**
 * Verwirft leere Rules (Wert erforderlich, aber leer), damit ein halb ausgefülltes Formular
 * nicht als ungültige Definition ans Backend geht. Gibt null zurück, wenn keine Rule übrig bleibt.
 */
export function normalizeGroup(group: SearchFilterGroup): SearchFilterGroup | null {
  const rules = group.rules.filter((rule) => isRuleComplete(rule));
  if (rules.length === 0) return null;
  return { logic: group.logic, rules, groups: [] };
}

function isRuleComplete(rule: SearchFilterRule): boolean {
  if (!operatorNeedsValue(rule.operator)) return true;
  const value = rule.value;
  if (Array.isArray(value)) {
    return value.length > 0 && value.every((entry) => `${entry ?? ''}`.trim() !== '');
  }
  return `${value ?? ''}`.trim() !== '';
}

export function buildRequest(input: {
  dataset: string;
  group: SearchFilterGroup;
  columns: string[];
  sort: SearchSort[];
  page: number;
  pageSize: number;
  propertyIds?: string[] | null;
}): AdvancedSearchRequest {
  return {
    dataset: input.dataset,
    schemaVersion: SEARCH_SCHEMA_VERSION,
    filters: normalizeGroup(input.group),
    columns: input.columns.length > 0 ? input.columns : null,
    sort: input.sort.length > 0 ? input.sort : null,
    page: input.page,
    pageSize: input.pageSize,
    propertyIds: input.propertyIds ?? null,
  };
}
